import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { ChatWindow } from "./components/ChatWindow";
import { RobotHead } from "./components/RobotHead";
import { AnalyticsEvents, initAnalytics, track } from "./lib/analytics";
import "./index.css";

const BUBBLE_MESSAGES = [
  "Hey 👋, Looking for help?",
  "What subject do you need help with?",
  "We can match you with the right tutor",
  "Australia’s top 1% tutors are ready to help",
  "Looking for private tutoring?",
  "We’ve got expert tutors for Years 1–12",
  "Want me to find the best tutor for you?",
  "It only takes a minute 🙂",
  "Let’s get you started"
];

export function init() {
  const container = document.getElementById("kis-way-chat-root");
  if (!container) return;

  const root = createRoot(container);
  root.render(<App />);
}

function App() {
  const [open, setOpen] = useState(false);
  const [bubbleIndex, setBubbleIndex] = useState(0);
  const [bubbleVisible, setBubbleVisible] = useState(true);

  useEffect(() => {
    initAnalytics();
    track(AnalyticsEvents.WidgetLoaded);
  }, []);

  const openChat = (source: "fab" | "bubble") => {
    track(AnalyticsEvents.ClickedFAB, { source });
    setOpen(true);
  };

  useEffect(() => {
    if (open || !bubbleVisible) return;
    const hide = () => {
      setBubbleVisible(false);
      setTimeout(() => {
        setBubbleIndex((i) => (i + 1) % BUBBLE_MESSAGES.length);
        setBubbleVisible(true);
      }, 800);
    };
    const t = setTimeout(hide, 4000);
    return () => clearTimeout(t);
  }, [open, bubbleIndex, bubbleVisible]);

  return (
    <>
      {!open && (
        <div className="kis-chat-fixed kis-chat-bottom-16 kis-chat-right-4 sm:kis-chat-right-6 kis-chat-z-[9998] kis-chat-flex kis-chat-items-start kis-chat-gap-3">
          {bubbleVisible && (
            <div
              className="kis-chat-max-w-[240px] kis-chat-mt-1 kis-chat-px-3 kis-chat-py-1.5 kis-chat-rounded-2xl kis-chat-rounded-br-sm kis-chat-bg-white kis-chat-shadow-[0_4px_20px_rgba(0,0,0,0.12)] kis-chat-border kis-chat-border-gray-100/80 kis-chat-bubble-float kis-chat-cursor-pointer"
              onClick={() => openChat("bubble")}
            >
              <p className="kis-chat-text-[13px] kis-chat-text-gray-800 kis-chat-font-bold kis-chat-leading-tight">
                {BUBBLE_MESSAGES[bubbleIndex]}
              </p>
            </div>
          )}
          <div
            onClick={() => openChat("fab")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && openChat("fab")}
            className="kis-chat-w-[76px] kis-chat-h-[76px] hover:kis-chat-scale-110 kis-chat-transition-all kis-chat-duration-200 kis-chat-flex kis-chat-items-center kis-chat-justify-center kis-chat-shrink-0 kis-chat-cursor-pointer"
            aria-label="Chat - find a tutor"
          >
            <RobotHead size={76} trackMouse />
          </div>
        </div>
      )}

      {open && (
        <div
          className="kis-chat-fixed kis-chat-bottom-20 kis-chat-right-4 sm:kis-chat-right-6 kis-chat-left-4 sm:kis-chat-left-auto kis-chat-z-[9999] sm:kis-chat-w-[400px] kis-chat-min-h-[420px] kis-chat-max-h-[min(650px,80vh)] kis-chat-bg-white kis-chat-rounded-2xl kis-chat-shadow-[0_8px_40px_rgba(0,0,0,0.16)] kis-chat-overflow-hidden kis-chat-flex kis-chat-flex-col kis-chat-widget-open kis-chat-border kis-chat-border-gray-100/50"
          role="dialog"
          aria-modal="true"
        >
          <ChatWindow onClose={() => setOpen(false)} />
        </div>
      )}
    </>
  );
}
